import { graphql, useStaticQuery } from "gatsby";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useCookies } from "react-cookie";

import { waitTillTrue } from "@theme/utils";

import { currentMsMarket } from "@ms/gatsby-site/src/config/navigation";

import type { MsMarketType } from "@ms/gatsby-site/src/types/navigation";
import type { SetStateAction } from "jotai";

export interface KetchSettings {
  essential_services: boolean;
  functional_cookies: boolean;
  performance_cookies: boolean;
  targeting_cookies: boolean;
  initialized: boolean;
}

/**
 * Ketch atom
 *
 * This defaults all cookies to false
 * and waits for ketch onConsent callback to update
 * the users consent.
 */
export const ketchAtom = atom<KetchSettings>({
  initialized: false,
  essential_services: false,
  functional_cookies: false,
  performance_cookies: false,
  targeting_cookies: false,
});

export const ketchLanguageMappings: { [k: string]: Record<string, string> } = {
  US: {
    es: "es-419",
  },
  CA: {
    fr: "fr-CA",
  },
  EU: {
    es: "es",
    fr: "fr",
    de: "de",
  },
};

const ALL_KETCH_JURISDICTIONS_QUERY = graphql`
  query AllKetchJurisdictions {
    allKetchJurisdiction {
      nodes {
        instance
        relationships {
          purposes {
            data {
              essential_services
              functional_cookies
              performance_cookies
              targeting_cookies
            }
          }
        }
      }
    }
  }
`;

/**
 * Get Ketch Fallback Settings
 *
 * This function is called if the onConsent callback is not available
 *
 * This will prevent pixels from being completely blocked if ketch fails to load again
 *
 * it will use the currentMsMarket to determine the current jurisdiction in ketch
 * and then use that to set the ketch cookies
 *
 * 'consent_optin' means users must opt in to the cookie. all other settings can be ignored
 * and we can default to the use accepting the cookie
 *
 *
 * @param allKetchJurisdiction
 * @param setKetchCookies
 */
const getKetchFallbackSettings = async (
  allKetchJurisdiction: { nodes: KetchJurisdiction[] },
  setKetchCookies: (update: SetStateAction<KetchSettings>) => void,
) => {
  const ketchJurisdictionMapping = {
    au: "AUS",
    ca: "CAN",
    eu: "gdpr",
    sg: "default",
    uk: "gdpr",
    za: "south_africa",
    us: "GEN",
    // CANT USE THE BELOW BECAUSE THEY ARE INSIDE OF THE US
    CALIFORNIA: "ccpa",
    COLORADO: "CO",
    UTAH: "UT",
    VIRGINIA: "VA",
  };

  const currentJurisdiction =
    allKetchJurisdiction.nodes.find(
      (node) => node.instance === ketchJurisdictionMapping[currentMsMarket as MsMarketType],
    )?.relationships.purposes.data ??
    // if the current jurisdiction is not found, default to the default jurisdiction
    allKetchJurisdiction.nodes.find((node) => node.instance === "default")?.relationships.purposes
      .data;

  setKetchCookies({
    initialized: true,
    essential_services: currentJurisdiction?.essential_services !== "consent_optin",
    functional_cookies: currentJurisdiction?.functional_cookies !== "consent_optin",
    performance_cookies: currentJurisdiction?.performance_cookies !== "consent_optin",
    targeting_cookies: currentJurisdiction?.targeting_cookies !== "consent_optin",
  });
};

/**
 * Use Ketch
 *
 * This hook allows us to track all ketch user statuses
 *
 * By adding an "onConsent" callback function, we can use a jotai atom
 * to track all the cookie statuses
 */
export const useKetch = () => {
  const setKetchCookies = useSetAtom(ketchAtom);

  const { allKetchJurisdiction } = useStaticQuery(ALL_KETCH_JURISDICTIONS_QUERY);

  const [cookies] = useCookies(["_ketch_consent_v1_"]);
  const ketchCookie = cookies._ketch_consent_v1_ ? String(cookies._ketch_consent_v1_) : undefined;

  // This effect is called on application load and checks if ketch cookie is present
  // if it is, it will update the ketchAtom with the decoded cookie values to make
  // speed up the process of getting users cookie consent status
  useEffect(() => {
    if (ketchCookie) {
      const ketchCookieData = JSON.parse(atob(ketchCookie));
      setKetchCookies({
        initialized: true,
        essential_services: ketchCookieData?.essential_services?.status === "granted",
        functional_cookies: ketchCookieData?.functional_cookies?.status === "granted",
        performance_cookies: ketchCookieData?.performance_cookies?.status === "granted",
        targeting_cookies: ketchCookieData?.targeting_cookies?.status === "granted",
      });
    }
  }, [ketchCookie, setKetchCookies]);

  // This effect is called on application load and waits for the ketch onConsent callback to be fetched
  // this event is slower, but will provide more up to date data
  useEffect(() => {
    waitTillTrue(
      () => typeof window.ketch === "function",
      () => {
        window.ketch?.("on", "consent", (c) => {
          setKetchCookies({
            initialized: true,
            essential_services: c.purposes?.essential_services ?? false,
            functional_cookies: c.purposes?.functional_cookies ?? false,
            performance_cookies: c.purposes?.performance_cookies ?? false,
            targeting_cookies: c.purposes?.targeting_cookies ?? false,
          });
        });
      },
      // fallback to default settings if ketch fails to load
      () => getKetchFallbackSettings(allKetchJurisdiction, setKetchCookies),
    );

    // this event triggers when the user updates their consent
    // We must reload the page to ensure all pixels are updated based on the users new consent
    waitTillTrue(
      () => typeof window.ketch === "function",
      () => {
        window.ketch?.("on", "userConsentUpdated", () => {
          window.location.reload();
        });
      },
    );
  }, [allKetchJurisdiction, setKetchCookies]);
};

interface KetchJurisdiction {
  instance: string;
  relationships: {
    purposes: {
      data: {
        essential_services: string;
        functional_cookies: string;
        performance_cookies: string;
        targeting_cookies: string;
      };
    };
  };
}

/**
 * Use Dy Active Consent
 *
 * This hook allows us to update the Dy Active Consent
 *
 * It uses ketchAtom to determine if the user has accepted performance cookies and sends that to DY
 */
export const useDyActiveConsent = () => {
  const { performance_cookies, initialized } = useAtomValue(ketchAtom);
  useEffect(() => {
    if (initialized && process.env.DY_SECTION_ID) {
      window.DYO?.ActiveConsent.updateConsentAcceptedStatus(performance_cookies);
      window.DY = window.DY || {}; // set DY as early as possible
      window.DY.userActiveConsent = { accepted: performance_cookies };
    }
  }, [initialized, performance_cookies]);
};

/**
 * QM ID Atom
 *
 * This atom is used to store the Quantum Metric ID
 */
export const qmIdAtom = atom<string | null>(null);

/**
 * Use QM ID
 *
 * This hook allows us to get the Quantum Metric ID
 *
 * We condense the id to help debugging
 * we only show first 4 and last 4 characters ie "d2aa-e73e"
 *
 * This is used in the footer to help debugging as well as being passed to shopify checkout
 * via shopify cart attributes
 */
export const useQmId = () => {
  const setQmId = useSetAtom(qmIdAtom);

  useEffect(() => {
    waitTillTrue(
      () => typeof window.QuantumMetricAPI?.getSessionID() === "string",
      () => {
        const sessionId = window.QuantumMetricAPI?.getSessionID();
        const condensedId = `${sessionId?.substring(0, 4)}-${sessionId?.substring(
          sessionId.length - 4,
        )}`;
        setQmId(condensedId);
      },
    );
  }, [setQmId]);
};

/**
 * Use Gladly
 *
 * This hook initializes the Gladly Chat SDK after the loader script
 * has been added via Gatsby's <Script /> component.
 *
 * It uses waitTillTrue to poll for window.Gladly to exist before
 * calling init(), providing retry logic with exponential backoff.
 */
export const useGladlyChat = () => {
  useEffect(() => {
    const gladlyAppId = process.env.GLADLY_CHAT_APP_ID;

    if (!gladlyAppId) {
      console.warn("Gladly Chat App ID is not configured.");

      return;
    }

    waitTillTrue(
      () => typeof window.Gladly?.init === "function",
      () => {
        window.Gladly?.init({
          appId: gladlyAppId,
          autoShowButton: true,
        })
          .then(() => console.info("Gladly chat initialized successfully"))
          .catch((error: unknown) => console.warn("Gladly chat init failed:", error));
      },
      () => console.warn("Gladly SDK failed to load after retries"),
    );
  }, []);
};
